import { Location } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { config } from '@app/core/app-config';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { NotificationType } from '@app/shared/interfaces';
import { CurrenciesService } from '@app/shared/services/currencies/currencies.service';
import { LanguageService } from '@app/shared/services/language/language.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { ScriptLoaderService } from '@app/shared/services/script-loader/script-loader.service';
import { TeamService } from '@app/shared/services/team/team.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { environment } from '@env/environment';
import { debounceTime, skip } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-team-detail',
  templateUrl: './team-detail.component.html',
  styleUrls: ['./team-detail.component.scss'],
})
export class TeamDetailComponent implements OnInit, AfterViewInit {
  member: any;

  loading: boolean;
  memberUuid: string;
  languages$ = this.languageService.getLanguages();
  currencies$ = this.currenciesService.getCurrencies();
  memberForm: UntypedFormGroup;
  saveStatus: number;
  addressLabel: string;
  possibleRoles: any;
  possibleProperties: any;
  dayArray = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  searchCriteriaProperties: string;
  deleting: boolean;
  newMode: boolean; // true if we're creating a new team buddy
  saving: boolean;

  config = config;

  autocomplete: any;

  constructor(
    private location: Location,
    private teamService: TeamService,
    private route: ActivatedRoute,
    private languageService: LanguageService,
    private currenciesService: CurrenciesService,
    private fb: UntypedFormBuilder,
    private propertiesService: PropertiesService,
    private toast: ToastNotificationsService,
    private dialog: MatDialog,
    private router: Router,
    private titleService: TitleService,
    private scriptLoader: ScriptLoaderService,
    private ref: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.titleService.manuallySet('Team member');

    this.memberUuid = this.route.snapshot.paramMap.get('id');

    if (!this.memberUuid) {
      this.titleService.manuallySet('Add a new team member');
      this.newMode = true;
      this.member = {
        name: '',
        first_name: '',
        last_name: '',
        is_company: false,
        company_name: '',
        email: '',
        phone_number: '',
        // address: '',
        language: 'en',
        country: '',
        currency: '',
        all_services: false,
        all_properties: true,
        properties: [],
        services: [],
        availability: [
          {
            day: 0,
            name: 'Monday',
            available: true,
            hour_start: 9,
            minute_start: 0,
            hour_end: 18,
            minute_end: 0,
          },
          {
            day: 1,
            name: 'Tuesday',
            available: true,
            hour_start: 9,
            minute_start: 0,
            hour_end: 18,
            minute_end: 0,
          },
          {
            day: 2,
            name: 'Wednesday',
            available: true,
            hour_start: 9,
            minute_start: 0,
            hour_end: 18,
            minute_end: 0,
          },
          {
            day: 3,
            name: 'Thursday',
            available: true,
            hour_start: 9,
            minute_start: 0,
            hour_end: 18,
            minute_end: 0,
          },
          {
            day: 4,
            name: 'Friday',
            available: true,
            hour_start: 9,
            minute_start: 0,
            hour_end: 18,
            minute_end: 0,
          },
          {
            day: 5,
            name: 'Saturday',
            available: true,
            hour_start: 9,
            minute_start: 0,
            hour_end: 18,
            minute_end: 0,
          },
          {
            day: 6,
            name: 'Sunday',
            available: true,
            hour_start: 9,
            minute_start: 0,
            hour_end: 18,
            minute_end: 0,
          },
        ],
      };
    }

    this.memberForm = this.fb.group({
      companyName: [''],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', []],
      phone: ['', []],
      address: ['', []],
      language: [this.newMode ? this.member.language : '', [Validators.required]],
      currency: ['', [Validators.required]],
    });

    this.teamService.getPossibleRoles().subscribe((res) => {
      this.possibleRoles = res;
      this.getTeamMember();
    });

    this.propertiesService.getScopingProperties([], null, null, 5000, false).subscribe((res) => {
      this.possibleProperties = res.data;
    });

    this.listenForFormChanges();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.loadGooglePlacesScript();
    }, 1000);
  }

  loadGooglePlacesScript() {
    this.scriptLoader
      .load({
        name: 'Google Places',
        src: `https://maps.googleapis.com/maps/api/js?key=${environment.google.places.apiKey}&libraries=places`,
      })
      .subscribe(() => {
        const input = document.getElementById('pac-input') as HTMLInputElement;
        this.autocomplete = new (google as any).maps.places.Autocomplete(input, {});

        this.autocomplete.addListener('place_changed', () => {
          this.fillInAddress();
        });
      });
  }

  fillInAddress() {
    // Get the place details from the autocomplete object.
    const place = this.autocomplete.getPlace();

    let address1 = '';
    let postcode = '';

    console.log(place);

    if (place.geometry.location.lat()) {
      this.member.address.latitude = place.geometry.location.lat();
    }

    if (place.geometry.location.lng()) {
      this.member.address.longitude = place.geometry.location.lng();
    }

    // Get each component of the address from the place details,
    // and then fill-in the corresponding field on the form.
    // place.address_components are google.maps.GeocoderAddressComponent objects
    // which are documented at http://goo.gle/3l5i5Mr
    for (const component of place.address_components) {
      // @ts-ignore remove once typings fixed
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number': {
          address1 = `${component.long_name} ${address1}`;
          break;
        }

        case 'route': {
          address1 += component.long_name;
          break;
        }

        case 'postal_code': {
          postcode = `${component.long_name}${postcode}`;
          break;
        }

        case 'postal_code_suffix': {
          postcode = `${postcode}-${component.long_name}`;
          break;
        }

        case 'postal_town': {
          this.member.address.city = component.long_name;
          break;
        }

        case 'locality':
          this.member.address.city = component.long_name;
          break;

        // case 'administrative_area_level_1': {
        //   this.region = component.short_name;
        //   break;
        // }

        case 'administrative_area_level_2': {
          // this.region = component.short_name;
          break;
        }

        case 'country':
          this.member.address.country = component.long_name;
          this.member.address.country_code = component.short_name;
          break;
      }
    }

    this.member.address.name = address1;

    this.member.address.postcode = postcode;

    setTimeout(() => {
      this.ref.markForCheck();
      this.updateMember();
    }, 1000);
  }

  listenForFormChanges() {
    this.memberForm.valueChanges.pipe(debounceTime(1000), skip(1)).subscribe((val) => {
      this.member.company_name = this.memberForm.get('companyName').value;
      this.member.is_company = this.memberForm.get('companyName').value ? true : false;
      this.member.first_name = this.memberForm.get('firstName').value;
      this.member.last_name = this.memberForm.get('lastName').value;
      this.member.email = this.memberForm.get('email').value;
      this.member.phone_number = this.memberForm.get('phone').value;
      this.member.language = this.memberForm.get('language').value;
      this.member.currency = this.memberForm.get('currency').value;

      this.updateMember();
    });
  }

  getTeamMember() {
    if (this.newMode) {
      return;
    }

    this.teamService.getTeamMember(this.memberUuid).subscribe((res: any) => {
      if (res.error) {
        this.router.navigate(['/operations/team']);
        return;
      }

      let member = res;
      member.services_labels = [];

      member.services.forEach((service) => {
        member.services_labels.push(this.possibleRoles.find((role) => role.id === service).name);
      });

      this.member = member;

      this.titleService.manuallySet(member.name);

      this.memberForm.get('companyName').setValue(this.member.company_name);
      this.memberForm.get('firstName').setValue(this.member.first_name);
      this.memberForm.get('lastName').setValue(this.member.last_name);
      this.memberForm.get('email').setValue(this.member.email);
      this.memberForm.get('phone').setValue(this.member.phone_number);
      this.memberForm.get('language').setValue(this.member.language);
      this.memberForm.get('currency').setValue(this.member.currency);

      this.addressLabel = `${this.member.address.name ? this.member.address.name + ' ' : ''}${this.member.address.city ? this.member.address.city + ' ' : ''
        }${this.member.address.country ? this.member.address.country + ' ' : ''}`;
    });
  }

  updateMember() {
    if (this.newMode) {
      return;
    }

    if (!this.member.all_properties && this.member.properties.length === 0) {
      this.toast.open('Please add at least 1 property for this team member', 'Dismiss', NotificationType.Error);

      return;
    }

    if (!this.member.all_services && this.member.services.length === 0) {
      this.toast.open('Please add at least 1 role for this team member', 'Dismiss', NotificationType.Error);

      return;
    }

    this.saveStatus = 1;

    this.teamService.updateTeamMember(this.memberUuid, this.member).subscribe((res) => {
      if (res.error) {
        this.saveStatus = -1;
      } else {
        this.saveStatus = 2;
        setTimeout(() => {
          this.saveStatus = null;
        }, 2000);
      }
    });
  }

  roleChange(role, event: MatCheckboxChange) {
    if (event.checked) {
      if (this.member.services.indexOf(role.id) === -1) {
        this.member.services.push(role.id);
        this.member.services_labels = [];

        this.member.services.forEach((service) => {
          this.member.services_labels.push(this.possibleRoles.find((role) => role.id === service).name);
        });
        this.updateMember();
      }
    } else {
      const index = this.member.services.findIndex((r) => r === role.id);

      if (index !== -1) {
        this.member.services.splice(index, 1);
        this.member.services_labels = [];

        this.member.services.forEach((service) => {
          this.member.services_labels.push(this.possibleRoles.find((role) => role.id === service).name);
        });
        this.updateMember();
      }
    }
  }

  allRolesChange(event: MatCheckboxChange) {
    this.member.all_services = event.checked;
    this.updateMember();
  }

  allPropertyChange(event: MatCheckboxChange) {
    this.member.all_properties = event.checked;
    this.updateMember();
  }

  propertyCheckboxChange(property, event: MatCheckboxChange) {
    if (event.checked) {
      if (this.member.properties.indexOf(property.id) === -1) {
        this.member.properties.push(property.id);
        this.updateMember();
      }
    } else {
      const index = this.member.properties.findIndex((r) => r === property.id);

      if (index !== -1) {
        this.member.properties.splice(index, 1);
        this.updateMember();
      }
    }
  }

  availabilityChange(index: number, event: MatCheckboxChange) {
    this.member.availability[index].available = event.checked;

    this.updateMember();
  }

  updateAvailabililityTime(dayIndex: number, which: 'start' | 'end', event: any) {
    this.member.availability[dayIndex]['hour_' + which] = event.hours;
    this.member.availability[dayIndex]['minute_' + which] = event.minutes;

    this.updateMember();
  }

  copiedToClipboard() {
    this.toast.open(`The iCal link has been copied to your clipboard`, 'Dismiss', NotificationType.Error, 2000);
  }

  sendInstructions() {
    this.teamService.sendIcalInstructions(this.memberUuid).subscribe((res: any) => {
      let message: string;

      if (res.success) {
        message = `We've emailed instructions to your team member`;
      } else {
        message = res.message;
      }

      this.toast.open(message, 'Dismiss', NotificationType.Success);
    });
  }

  deletePrompt() {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: 'Team member',
        identifier: this.member.name,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteTeam();
      }
    });
  }

  resetCalendar() {
    this.teamService.resetCalendarAccess(this.memberUuid).subscribe((res) => {
      this.member.ical_suffix = res;

      this.toast.open(`Success - we've updated the iCal feed URL`, 'Dismiss', NotificationType.Success);
    });
  }

  createMember() {
    this.saving = true;
    this.teamService.createTeamMember(this.member).subscribe((res) => {
      this.saving = false;

      if (res.error) {
        return;
      } else {
        this.router.navigate(['/operations/team/member/' + res.uuid]);
      }
    });
  }

  deleteTeam() {
    this.deleting = true;

    this.teamService.deleteTeamMember(this.memberUuid).subscribe((res) => {
      this.deleting = false;

      if (res.error) {
        alert('There was an error deleting this team member');
      } else {
        this.location.back();
      }
    });
  }

  goBack() {
    this.router.navigate(['/operations/team']);
  }
}
